import { io, Socket } from "socket.io-client";
import type {
  CallAcceptedEvent,
  CallDeclinedEvent,
  CallEndedEvent,
  OfferEvent,
  AnswerEvent,
  IceCandidateEvent,
  PatientAvailabilityEvent,
} from "@virtual-care/shared";
import { requireEnv } from "@virtual-care/shared";

export class SignalingService {
  private socket: Socket | null = null;
  private socketUrl: string;
  private callbacks: Map<string, Set<Function>> = new Map();

  constructor() {
    this.socketUrl = requireEnv(process.env.VUE_APP_SOCKET_URL, "VUE_APP_SOCKET_URL");
  }

  connect(userId: string): void {
    if (this.socket?.connected) {
      return;
    }

    const token = localStorage.getItem('vc_jwt_token')

    this.socket = io(this.socketUrl, {
      path: "/socket.io",
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
      auth: {
        token,
      },
    });

    this.socket.on("connect", () => {
      console.log("[SignalingService] Socket connected:", this.socket?.id);
      this.socket?.emit("register_nurse", userId);
    });

    this.socket.on("disconnect", () => {
      console.log("[SignalingService] Socket disconnected");
    });

    this.setupEventHandlers();
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  private setupEventHandlers(): void {
    if (!this.socket) return;

    this.socket.on("call_accepted", (event: CallAcceptedEvent) => {
      this.emit("call_accepted", event);
    });

    this.socket.on("call_declined", (event: CallDeclinedEvent) => {
      this.emit("call_declined", event);
    });

    this.socket.on("call_ended", (event: CallEndedEvent) => {
      this.emit("call_ended", event);
    });

    this.socket.on("patient_availability", (event: PatientAvailabilityEvent) => {
      console.log("[SignalingService] Received patient_availability event:", event);
      this.emit("patient_availability", event);
    });

    this.socket.on("offer", (event: OfferEvent) => {
      this.emit("offer", event);
    });

    this.socket.on("answer", (event: AnswerEvent) => {
      this.emit("answer", event);
    });

    this.socket.on("ice-candidate", (event: IceCandidateEvent) => {
      this.emit("ice-candidate", event);
    });
  }

  on(event: string, callback: Function): void {
    if (!this.callbacks.has(event)) {
      this.callbacks.set(event, new Set());
    }
    this.callbacks.get(event)!.add(callback);
  }

  off(event: string, callback?: Function): void {
    if (!callback) {
      this.callbacks.delete(event);
      return;
    }
    this.callbacks.get(event)?.delete(callback);
  }

  private emit(event: string, data: unknown): void {
    const callbacks = this.callbacks.get(event);
    if (callbacks) {
      callbacks.forEach((cb) => cb(data));
    }
  }

  sendOffer(sessionId: string, offer: RTCSessionDescriptionInit, from: string): void {
    const event: OfferEvent = { type: "offer", sessionId, offer, from };
    this.socket?.emit("offer", event);
  }

  sendAnswer(sessionId: string, answer: RTCSessionDescriptionInit, from: string): void {
    const event: AnswerEvent = { type: "answer", sessionId, answer, from };
    this.socket?.emit("answer", event);
  }

  sendIceCandidate(sessionId: string, candidate: RTCIceCandidateInit, from: string): void {
    const event: IceCandidateEvent = { type: "ice-candidate", sessionId, candidate, from };
    this.socket?.emit("ice-candidate", event);
  }

  joinRoom(roomId: string): void {
    this.socket?.emit("register_room", roomId);
  }

  joinSession(sessionId: string): void {
    this.socket?.emit("join_session", sessionId);
  }

  notifyCallEnded(sessionId: string, endedBy: string, reason?: string): void {
    const event: CallEndedEvent = { type: "call_ended", sessionId, endedBy, reason };
    this.socket?.emit("call_ended", event);
  }
}

export const signalingService = new SignalingService();
